import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  Download, 
  Upload,
  BookOpen,
  FileText,
  Loader2,
  Shield,
  AlertTriangle,
  Check,
  X,
  Search,
  Activity,
  Database,
  Gauge,
  RefreshCw,
  Server,
  Zap,
  Mail,
  Bell,
  Settings,
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  Clock,
  Users,
  User,
  Crown,
  Key,
  ChevronLeft,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [activeTab, setActiveTab] = useState("quizzes");
  
  // Quiz state
  const [quizzes, setQuizzes] = useState([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quizForm, setQuizForm] = useState({
    title: "", description: "", category: "", difficulty: "medium", time_limit: 10, is_mock_exam: false
  });
  const [questionForm, setQuestionForm] = useState({
    text: "", question_type: "multiple_choice", options: ["", "", "", ""], correct_answer: "", explanation: ""
  });
  const [editingQuestion, setEditingQuestion] = useState(null);
  
  // Dictionary state
  const [dictionary, setDictionary] = useState({ acronyms: [], memory_aids: [], terms: [], core_concepts: [] });
  const [loadingDictionary, setLoadingDictionary] = useState(false);
  const [showAcronymDialog, setShowAcronymDialog] = useState(false);
  const [showTermDialog, setShowTermDialog] = useState(false);
  const [acronymForm, setAcronymForm] = useState({ acronym: "", full: "", description: "" });
  const [termForm, setTermForm] = useState({ term: "", definition: "", citation: "" });
  const [editingAcronym, setEditingAcronym] = useState(null);
  const [editingTerm, setEditingTerm] = useState(null);
  
  // Search
  const [searchTerm, setSearchTerm] = useState("");
  
  // Import state
  const [importing, setImporting] = useState(false);
  
  // Server status state
  const [serverStats, setServerStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [lastStatsUpdate, setLastStatsUpdate] = useState(null);
  const [sendingTestAlert, setSendingTestAlert] = useState(false);
  
  // Mock exam expansion state
  const [mockExamStatus, setMockExamStatus] = useState(null);
  const [expandingMocks, setExpandingMocks] = useState(false);
  
  // Vignettes import state
  const [vignettesStatus, setVignettesStatus] = useState(null);
  const [importingVignettes, setImportingVignettes] = useState(false);
  const [quizzesStatus, setQuizzesStatus] = useState(null);
  const [importingQuizzes, setImportingQuizzes] = useState(false);
  const [flashcardsStatus, setFlashcardsStatus] = useState(null);
  const [importingFlashcards, setImportingFlashcards] = useState(false);
  
  // Vignettes adjustment state
  const [showVignetteAdjust, setShowVignetteAdjust] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [targetPercentage, setTargetPercentage] = useState(75);
  const [adjustingVignettes, setAdjustingVignettes] = useState(false);
  
  // Session management state
  const [sessionSettings, setSessionSettings] = useState(null);
  const [activeSessions, setActiveSessions] = useState(null);
  const [savingSessionSettings, setSavingSessionSettings] = useState(false);

  // User management state
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isAuthenticated) {
        setCheckingAdmin(false);
        return;
      }
      
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
      } catch (error) {
        setIsAdmin(false);
      } finally {
        setCheckingAdmin(false);
      }
    };
    
    if (!authLoading) {
      checkAdminStatus();
    }
  }, [isAuthenticated, authLoading]);

  // Load quizzes
  const loadQuizzes = async () => {
    setLoadingQuizzes(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/quizzes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizzes(response.data);
    } catch (error) {
      toast.error("Failed to load quizzes");
    } finally {
      setLoadingQuizzes(false);
    }
  };

  // Load dictionary
  const loadDictionary = async () => {
    setLoadingDictionary(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/dictionary/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDictionary({
        acronyms: response.data.acronyms || [],
        memory_aids: response.data.memory_aids || [],
        terms: response.data.terms || [],
        core_concepts: response.data.core_concepts || []
      });
    } catch (error) {
      toast.error("Failed to load dictionary");
    } finally {
      setLoadingDictionary(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadQuizzes();
      loadDictionary();
      loadServerStats();
      
      // Auto-refresh server stats every 30 seconds
      const statsInterval = setInterval(loadServerStats, 30000);
      return () => clearInterval(statsInterval);
    }
  }, [isAdmin]);

  // Load server stats
  const loadServerStats = async () => {
    setLoadingStats(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServerStats(response.data);
      setLastStatsUpdate(new Date());
    } catch (error) {
      console.error("Failed to load server stats:", error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Send test alert email
  const sendTestAlert = async () => {
    setSendingTestAlert(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/alerts/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'success') {
        toast.success("Test alert email sent successfully!");
      } else if (response.data.status === 'not_configured') {
        toast.info(
          <div>
            <p className="font-medium">Email alerts not configured</p>
            <p className="text-sm mt-1">Get a Resend API key at resend.com to enable email alerts.</p>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error(response.data.message || "Failed to send test alert");
      }
    } catch (error) {
      toast.error("Failed to send test alert: " + (error.response?.data?.detail || error.message));
    } finally {
      setSendingTestAlert(false);
    }
  };

  // Mock Exam Expansion
  const loadMockExamStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/mock-exam-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMockExamStatus(response.data);
    } catch (error) {
      console.error("Failed to load mock exam status:", error);
    }
  };

  const expandMockExams = async () => {
    setExpandingMocks(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/expand-mock-exams`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const expanded = response.data.results.filter(r => r.status === "expanded");
      const alreadyExpanded = response.data.results.filter(r => r.status === "already_expanded");
      
      if (expanded.length > 0) {
        toast.success(
          <div>
            <p className="font-medium">Mock Exams Expanded!</p>
            <p className="text-sm mt-1">Added 35 questions to {expanded.length} exam(s).</p>
            <p className="text-sm">Total: {response.data.total_mock_exam_questions} questions</p>
          </div>,
          { duration: 5000 }
        );
      } else if (alreadyExpanded.length > 0) {
        toast.info("Mock exams already expanded to 185 questions each.");
      }
      
      loadMockExamStatus();
      loadQuizzes();
    } catch (error) {
      toast.error("Failed to expand mock exams: " + (error.response?.data?.detail || error.message));
    } finally {
      setExpandingMocks(false);
    }
  };

  // Vignettes functions
  const loadVignettesStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/vignettes-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVignettesStatus(response.data);
    } catch (error) {
      console.error("Failed to load vignettes status:", error);
    }
  };

  const importVignettes = async () => {
    setImportingVignettes(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/import-vignettes`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const imported = response.data.results.filter(r => r.status === "imported");
      const totalAdded = imported.reduce((sum, r) => sum + r.vignettes_added, 0);
      
      if (totalAdded > 0) {
        toast.success(
          <div>
            <p className="font-medium">Clinical Vignettes Imported!</p>
            <p className="text-sm mt-1">Added {totalAdded} vignettes across {imported.length} exam(s).</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.info("All vignettes already imported.");
      }
      
      loadVignettesStatus();
    } catch (error) {
      toast.error("Failed to import vignettes: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingVignettes(false);
    }
  };

  // Quizzes functions
  const loadQuizzesStatus = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/quizzes-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuizzesStatus(response.data);
    } catch (error) {
      console.error("Failed to load quizzes status:", error);
    }
  };

  const importQuizzes = async () => {
    setImportingQuizzes(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/import-quizzes`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.imported > 0) {
        toast.success(
          <div>
            <p className="font-medium">Quizzes Imported!</p>
            <p className="text-sm mt-1">Added {response.data.imported} quizzes to the database.</p>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.info("Quizzes already imported.");
      }
      
      loadQuizzesStatus();
    } catch (error) {
      toast.error("Failed to import quizzes: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingQuizzes(false);
    }
  };

  const importFlashcards = async () => {
    setImportingFlashcards(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/import-flashcards`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.imported > 0) {
        toast.success(
          <div>
            <p className="font-medium">Flashcards Imported!</p>
            <p className="text-sm mt-1">Added {response.data.imported} flashcards to the database.</p>
          </div>,
          { duration: 5000 }
        );
        setFlashcardsStatus({ imported: true, count: response.data.imported });
      } else {
        toast.info("Flashcards already imported.");
      }
    } catch (error) {
      toast.error("Failed to import flashcards: " + (error.response?.data?.detail || error.message));
    } finally {
      setImportingFlashcards(false);
    }
  };

  // Adjust vignettes function
  const adjustVignettes = async () => {
    if (!selectedExam) return;
    
    setAdjustingVignettes(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/adjust-vignettes`, {
        exam_id: selectedExam.id,
        target_percentage: targetPercentage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const result = response.data;
      
      if (result.action === "increased") {
        toast.success(
          <div>
            <p className="font-medium">Vignettes Increased!</p>
            <p className="text-sm">Added {result.vignettes_added} vignettes ({result.new_percentage}%)</p>
          </div>
        );
      } else if (result.action === "decreased") {
        toast.success(
          <div>
            <p className="font-medium">Vignettes Decreased!</p>
            <p className="text-sm">Removed {result.vignettes_removed} vignettes ({result.new_percentage}%)</p>
          </div>
        );
      } else {
        toast.info("No change needed - already at target percentage.");
      }
      
      setShowVignetteAdjust(false);
      loadVignettesStatus();
    } catch (error) {
      toast.error("Failed to adjust vignettes: " + (error.response?.data?.detail || error.message));
    } finally {
      setAdjustingVignettes(false);
    }
  };

  // Load mock exam status when server tab is active
  useEffect(() => {
    if (activeTab === "server" && isAdmin) {
      loadMockExamStatus();
      loadVignettesStatus();
      loadQuizzesStatus();
      loadSessionSettings();
      loadActiveSessions();
    }
  }, [activeTab, isAdmin]);

  // Load users when users tab is active
  useEffect(() => {
    if (activeTab === "users" && isAdmin) {
      loadUsers(1, "");
    }
  }, [activeTab, isAdmin]);

  // Session management functions
  const loadSessionSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/session-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessionSettings(response.data);
    } catch (error) {
      console.error("Failed to load session settings:", error);
    }
  };

  const loadActiveSessions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/active-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveSessions(response.data);
    } catch (error) {
      console.error("Failed to load active sessions:", error);
    }
  };

  const saveSessionSettings = async () => {
    setSavingSessionSettings(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/session-settings`, sessionSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Session settings saved!");
      loadActiveSessions();
    } catch (error) {
      toast.error("Failed to save session settings");
    } finally {
      setSavingSessionSettings(false);
    }
  };

  const forceLogoutUser = async (userId, email) => {
    if (!confirm(`Force logout all sessions for ${email}?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/force-logout-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Logged out ${response.data.sessions_terminated} sessions`);
      loadActiveSessions();
    } catch (error) {
      toast.error("Failed to force logout user");
    }
  };

  const forceLogoutSession = async (userId, sessionIndex, deviceInfo) => {
    if (!confirm(`Logout this session (${deviceInfo})?`)) return;
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/admin/force-logout-session/${userId}/${sessionIndex}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Session terminated: ${response.data.device}`);
      loadActiveSessions();
    } catch (error) {
      toast.error("Failed to logout session");
    }
  };

  // User Management Functions
  const loadUsers = async (page = 1, search = "") => {
    setUsersLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
      setUsersPage(response.data.page);
    } catch (error) {
      console.error("Load users error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to load users");
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUserSearch = (e) => {
    e.preventDefault();
    loadUsers(1, userSearch);
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setShowUserDialog(true);
    } catch (error) {
      toast.error("Failed to load user details");
    }
  };

  const resetUserPassword = async () => {
    if (!selectedUser || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setResettingPassword(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/users/${selectedUser.user.id}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setShowResetPasswordDialog(false);
      setNewPassword("");
      loadUsers(usersPage, userSearch);
    } catch (error) {
      toast.error("Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  const toggleUserPremium = async (userId, email) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/admin/users/${userId}/toggle-premium`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      loadUsers(usersPage, userSearch);
      if (selectedUser && selectedUser.user.id === userId) {
        setSelectedUser({
          ...selectedUser,
          user: { ...selectedUser.user, is_premium: response.data.is_premium }
        });
      }
    } catch (error) {
      toast.error("Failed to update premium status");
    }
  };

  const deleteUser = async (userId, email) => {
    if (!confirm(`Are you sure you want to delete user ${email}? This will remove ALL their data and cannot be undone.`)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User deleted successfully");
      setShowUserDialog(false);
      setSelectedUser(null);
      loadUsers(usersPage, userSearch);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Quiz CRUD
  const handleSaveQuiz = async () => {
    try {
      const token = localStorage.getItem("token");
      if (selectedQuiz) {
        await axios.put(`${API}/admin/quizzes/${selectedQuiz.id}`, quizForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Quiz updated successfully");
      } else {
        await axios.post(`${API}/admin/quizzes`, quizForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Quiz created successfully");
      }
      setShowQuizDialog(false);
      resetQuizForm();
      loadQuizzes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quiz");
    }
  };

  const handleDeleteQuiz = async () => {
    if (!deleteTarget) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/quizzes/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quiz deleted successfully");
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      loadQuizzes();
    } catch (error) {
      toast.error("Failed to delete quiz");
    }
  };

  // Question CRUD
  const handleSaveQuestion = async () => {
    if (!selectedQuiz) return;
    
    const questionData = {
      ...questionForm,
      options: questionForm.options.filter(o => o.trim() !== "")
    };
    
    try {
      const token = localStorage.getItem("token");
      if (editingQuestion) {
        await axios.put(
          `${API}/admin/quizzes/${selectedQuiz.id}/questions/${editingQuestion.id}`,
          questionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Question updated successfully");
      } else {
        await axios.post(
          `${API}/admin/quizzes/${selectedQuiz.id}/questions`,
          questionData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Question added successfully");
      }
      setShowQuestionDialog(false);
      resetQuestionForm();
      loadQuizzes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save question");
    }
  };

  const handleDeleteQuestion = async (quizId, questionId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/quizzes/${quizId}/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Question deleted successfully");
      loadQuizzes();
    } catch (error) {
      toast.error("Failed to delete question");
    }
  };

  // Acronym CRUD
  const handleSaveAcronym = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingAcronym) {
        await axios.put(`${API}/admin/dictionary/acronyms/${editingAcronym.acronym}`, acronymForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Acronym updated successfully");
      } else {
        await axios.post(`${API}/admin/dictionary/acronyms`, acronymForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Acronym added successfully");
      }
      setShowAcronymDialog(false);
      setAcronymForm({ acronym: "", full: "", description: "" });
      setEditingAcronym(null);
      loadDictionary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save acronym");
    }
  };

  const handleDeleteAcronym = async (acronym) => {
    if (!confirm(`Delete acronym "${acronym}"?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/dictionary/acronyms/${acronym}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Acronym deleted");
      loadDictionary();
    } catch (error) {
      toast.error("Failed to delete acronym");
    }
  };

  // Term CRUD
  const handleSaveTerm = async () => {
    try {
      const token = localStorage.getItem("token");
      if (editingTerm) {
        await axios.put(`${API}/admin/dictionary/terms/${encodeURIComponent(editingTerm.term)}`, termForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Term updated successfully");
      } else {
        await axios.post(`${API}/admin/dictionary/terms`, termForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Term added successfully");
      }
      setShowTermDialog(false);
      setTermForm({ term: "", definition: "", citation: "" });
      setEditingTerm(null);
      loadDictionary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save term");
    }
  };

  const handleDeleteTerm = async (term) => {
    if (!confirm(`Delete term "${term}"?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/dictionary/terms/${encodeURIComponent(term)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Term deleted");
      loadDictionary();
    } catch (error) {
      toast.error("Failed to delete term");
    }
  };

  // Export functions
  const handleExportQuizzes = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/export/quizzes`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'quizzes_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Quizzes exported successfully");
    } catch (error) {
      toast.error("Failed to export quizzes");
    }
  };

  const handleExportDictionary = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/admin/export/dictionary`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'dictionary_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Dictionary exported successfully");
    } catch (error) {
      toast.error("Failed to export dictionary");
    }
  };

  // Import functions
  const handleImportQuizzes = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/import/quizzes`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      loadQuizzes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import quizzes");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleImportDictionary = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/admin/import/dictionary`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(response.data.message);
      loadDictionary();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import dictionary");
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  // Helper functions
  const resetQuizForm = () => {
    setQuizForm({ title: "", description: "", category: "", difficulty: "medium", time_limit: 10, is_mock_exam: false });
    setSelectedQuiz(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({ text: "", question_type: "multiple_choice", options: ["", "", "", ""], correct_answer: "", explanation: "" });
    setEditingQuestion(null);
  };

  const openEditQuiz = (quiz) => {
    setSelectedQuiz(quiz);
    setQuizForm({
      title: quiz.title,
      description: quiz.description,
      category: quiz.category,
      difficulty: quiz.difficulty,
      time_limit: quiz.time_limit,
      is_mock_exam: quiz.is_mock_exam || false
    });
    setShowQuizDialog(true);
  };

  const openEditQuestion = (quiz, question) => {
    setSelectedQuiz(quiz);
    setEditingQuestion(question);
    setQuestionForm({
      text: question.text,
      question_type: question.question_type,
      options: [...question.options, "", "", "", ""].slice(0, 4),
      correct_answer: question.correct_answer,
      explanation: question.explanation || ""
    });
    setShowQuestionDialog(true);
  };

  // Filter quizzes by search
  const filteredQuizzes = quizzes.filter(q => 
    q.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    q.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter acronyms by search
  const filteredAcronyms = dictionary.acronyms.filter(a =>
    a.acronym.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.full.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filter terms by search
  const filteredTerms = dictionary.terms.filter(t =>
    t.term.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Loading state
  if (authLoading || checkingAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <Shield className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Authentication Required</h1>
          <p className="text-slate-600 mb-6">Please log in to access the admin panel.</p>
          <Button onClick={() => navigate("/auth")} className="bg-violet-600 hover:bg-violet-700">
            Log In
          </Button>
        </div>
      </div>
    );
  }

  // Not admin
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-6">You don't have admin privileges to access this page.</p>
          <Button onClick={() => navigate("/")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-violet-600" />
                <h1 className="text-xl font-bold text-slate-800">Admin Panel</h1>
              </div>
            </div>
            <Badge className="bg-violet-100 text-violet-700">{user?.email}</Badge>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="quizzes" className="px-6">
              <BookOpen className="w-4 h-4 mr-2" />
              Quizzes ({quizzes.length})
            </TabsTrigger>
            <TabsTrigger value="dictionary" className="px-6">
              <FileText className="w-4 h-4 mr-2" />
              Dictionary
            </TabsTrigger>
            <TabsTrigger value="server" className="px-6">
              <Server className="w-4 h-4 mr-2" />
              Server Status
            </TabsTrigger>
            <TabsTrigger value="users" className="px-6">
              <Users className="w-4 h-4 mr-2" />
              Users ({usersTotal})
            </TabsTrigger>
          </TabsList>

          {/* Quizzes Tab */}
          <TabsContent value="quizzes">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {/* Actions Bar */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Button onClick={() => { resetQuizForm(); setShowQuizDialog(true); }} className="bg-violet-600 hover:bg-violet-700">
                    <Plus className="w-4 h-4 mr-2" />
                    New Quiz
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleExportQuizzes}>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                  <label className="cursor-pointer">
                    <Button variant="outline" asChild disabled={importing}>
                      <span>
                        {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Import CSV
                      </span>
                    </Button>
                    <input type="file" accept=".csv" className="hidden" onChange={handleImportQuizzes} />
                  </label>
                </div>
              </div>

              {/* Quiz List */}
              {loadingQuizzes ? (
                <div className="p-8 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-600" />
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {filteredQuizzes.map((quiz) => (
                    <div key={quiz.id} className="p-4 hover:bg-slate-50">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-800">{quiz.title}</h3>
                            {quiz.is_mock_exam && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">Mock Exam</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">{quiz.description}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Category: {quiz.category}</span>
                            <span>Difficulty: {quiz.difficulty}</span>
                            <span>Questions: {quiz.question_count || quiz.questions?.length || 0}</span>
                            <span>Time: {quiz.time_limit} min</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="ghost" onClick={() => openEditQuiz(quiz)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setDeleteTarget(quiz); setShowDeleteDialog(true); }}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {/* Questions Preview */}
                      <div className="mt-4 pl-4 border-l-2 border-slate-200">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-slate-600">Questions</span>
                          <Button size="sm" variant="outline" onClick={() => { setSelectedQuiz(quiz); resetQuestionForm(); setShowQuestionDialog(true); }}>
                            <Plus className="w-3 h-3 mr-1" />
                            Add Question
                          </Button>
                        </div>
                        <div className="space-y-2 max-h-40 overflow-y-auto">
                          {quiz.questions?.slice(0, 5).map((q, i) => (
                            <div key={q.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2">
                              <span className="text-slate-700 truncate flex-1 mr-4">
                                {i + 1}. {q.text.substring(0, 80)}{q.text.length > 80 ? "..." : ""}
                              </span>
                              <div className="flex items-center gap-1">
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEditQuestion(quiz, q)}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteQuestion(quiz.id, q.id)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          {quiz.questions?.length > 5 && (
                            <p className="text-xs text-slate-500 pl-3">...and {quiz.questions.length - 5} more questions</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Dictionary Tab */}
          <TabsContent value="dictionary">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Acronyms */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Acronyms ({dictionary.acronyms.length})</h3>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => { setEditingAcronym(null); setAcronymForm({ acronym: "", full: "", description: "" }); setShowAcronymDialog(true); }}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                  {filteredAcronyms.map((item) => (
                    <div key={item.acronym} className="p-3 hover:bg-slate-50 flex items-center justify-between">
                      <div>
                        <span className="font-mono font-bold text-violet-600">{item.acronym}</span>
                        <span className="text-slate-600 ml-2">- {item.full}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                          setEditingAcronym(item);
                          setAcronymForm({ acronym: item.acronym, full: item.full, description: item.description });
                          setShowAcronymDialog(true);
                        }}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteAcronym(item.acronym)}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dictionary Terms */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Dictionary Terms ({dictionary.terms.length})</h3>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={() => { setEditingTerm(null); setTermForm({ term: "", definition: "", citation: "" }); setShowTermDialog(true); }}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                  {filteredTerms.map((item) => (
                    <div key={item.term} className="p-3 hover:bg-slate-50 flex items-center justify-between">
                      <div className="flex-1 mr-2">
                        <span className="font-medium text-slate-800">{item.term}</span>
                        <p className="text-xs text-slate-500 truncate">{item.definition.substring(0, 60)}...</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => {
                          setEditingTerm(item);
                          setTermForm({ term: item.term, definition: item.definition, citation: item.citation || "" });
                          setShowTermDialog(true);
                        }}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleDeleteTerm(item.term)}>
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Export/Import for Dictionary */}
            <div className="mt-6 flex items-center gap-4 justify-end">
              <Button variant="outline" onClick={handleExportDictionary}>
                <Download className="w-4 h-4 mr-2" />
                Export Dictionary CSV
              </Button>
              <label className="cursor-pointer">
                <Button variant="outline" asChild disabled={importing}>
                  <span>
                    {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Import Dictionary CSV
                  </span>
                </Button>
                <input type="file" accept=".csv" className="hidden" onChange={handleImportDictionary} />
              </label>
            </div>
          </TabsContent>

          {/* Server Status Tab */}
          <TabsContent value="server">
            <div className="space-y-6">
              {/* Loading state */}
              {loadingStats && !serverStats && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                  <span className="ml-2 text-slate-600">Loading server stats...</span>
                </div>
              )}
              
              {/* Header with refresh */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">Server Performance Monitor</h2>
                  <p className="text-sm text-slate-500">
                    Real-time cache and rate limiting statistics
                    {lastStatsUpdate && (
                      <span className="ml-2">
                        • Last updated: {lastStatsUpdate.toLocaleTimeString()}
                      </span>
                    )}
                  </p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={loadServerStats}
                  disabled={loadingStats}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Status Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Server Status */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${serverStats?.status === 'operational' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      <Activity className={`w-5 h-5 ${serverStats?.status === 'operational' ? 'text-emerald-600' : 'text-amber-600'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Server Status</h3>
                      <p className="text-sm text-slate-500">API Health</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${
                      serverStats?.status === 'operational' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${
                        serverStats?.status === 'operational' ? 'bg-emerald-500' : 'bg-amber-500'
                      }`} />
                      {serverStats?.status === 'operational' ? 'Operational' : 'Loading...'}
                    </span>
                  </div>
                </div>

                {/* Cache Entries */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-violet-100">
                      <Database className="w-5 h-5 text-violet-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Cache Entries</h3>
                      <p className="text-sm text-slate-500">In-Memory Cache</p>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800">
                    {serverStats?.cache?.total_entries ?? '-'}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Active cached items</p>
                </div>

                {/* AI Cache */}
                <div className="bg-white rounded-xl border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <Zap className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">AI Explanations</h3>
                      <p className="text-sm text-slate-500">Cached Responses</p>
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-slate-800">
                    {serverStats?.ai_cache?.cached_explanations ?? '-'}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    ~{serverStats?.ai_cache?.estimated_memory_mb?.toFixed(2) ?? '0'} MB memory
                  </p>
                </div>

                {/* Rate Limit Blocks */}
                <div className={`bg-white rounded-xl border p-6 ${
                  (serverStats?.rate_limit_blocks?.total_blocks_last_hour ?? 0) > 20 
                    ? 'border-red-300 bg-red-50' 
                    : 'border-slate-200'
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-2 rounded-lg ${
                      (serverStats?.rate_limit_blocks?.total_blocks_last_hour ?? 0) > 20 
                        ? 'bg-red-100' 
                        : 'bg-slate-100'
                    }`}>
                      <Shield className={`w-5 h-5 ${
                        (serverStats?.rate_limit_blocks?.total_blocks_last_hour ?? 0) > 20 
                          ? 'text-red-600' 
                          : 'text-slate-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Rate Blocks</h3>
                      <p className="text-sm text-slate-500">Last Hour</p>
                    </div>
                  </div>
                  <div className={`text-3xl font-bold ${
                    (serverStats?.rate_limit_blocks?.total_blocks_last_hour ?? 0) > 20 
                      ? 'text-red-600' 
                      : 'text-slate-800'
                  }`}>
                    {serverStats?.rate_limit_blocks?.total_blocks_last_hour ?? 0}
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {serverStats?.rate_limit_blocks?.unique_blocked_identifiers ?? 0} unique sources
                  </p>
                </div>
              </div>

              {/* Detailed Info Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cache Keys */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Database className="w-4 h-4 text-violet-600" />
                      Active Cache Keys
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Currently cached data (first 20 shown)
                    </p>
                  </div>
                  <div className="p-4 max-h-64 overflow-y-auto">
                    {serverStats?.cache?.keys?.length > 0 ? (
                      <div className="space-y-2">
                        {serverStats.cache.keys.map((key, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <span className="w-2 h-2 rounded-full bg-violet-400" />
                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono text-slate-700 truncate flex-1">
                              {key}
                            </code>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500 text-center py-4">
                        No cached items yet
                      </p>
                    )}
                  </div>
                </div>

                {/* Session Management */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-indigo-600" />
                      Session Management
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Control concurrent device logins
                    </p>
                  </div>
                  <div className="p-4 space-y-4">
                    {sessionSettings && (
                      <>
                        {/* Max Devices */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Maximum Devices per User
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="range"
                              min="1"
                              max="10"
                              value={sessionSettings.max_devices}
                              onChange={(e) => setSessionSettings({
                                ...sessionSettings,
                                max_devices: parseInt(e.target.value)
                              })}
                              className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                            />
                            <span className="w-12 text-center font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
                              {sessionSettings.max_devices}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-slate-400 mt-1">
                            <span>1</span>
                            <span>5</span>
                            <span>10</span>
                          </div>
                        </div>

                        {/* Auto Logout Toggle */}
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Auto-logout oldest session</p>
                            <p className="text-xs text-slate-500">When limit exceeded, automatically log out the oldest device</p>
                          </div>
                          <button
                            onClick={() => setSessionSettings({
                              ...sessionSettings,
                              auto_logout_oldest: !sessionSettings.auto_logout_oldest
                            })}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              sessionSettings.auto_logout_oldest ? 'bg-indigo-600' : 'bg-slate-300'
                            }`}
                          >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              sessionSettings.auto_logout_oldest ? 'translate-x-6' : 'translate-x-1'
                            }`} />
                          </button>
                        </div>

                        {/* Session Timeout */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Session Timeout (hours)
                          </label>
                          <select
                            value={sessionSettings.session_timeout_hours}
                            onChange={(e) => setSessionSettings({
                              ...sessionSettings,
                              session_timeout_hours: parseInt(e.target.value)
                            })}
                            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="0">No timeout</option>
                            <option value="1">1 hour</option>
                            <option value="4">4 hours</option>
                            <option value="8">8 hours</option>
                            <option value="24">24 hours</option>
                            <option value="72">3 days</option>
                            <option value="168">7 days</option>
                          </select>
                        </div>

                        {/* Save Button */}
                        <Button
                          onClick={saveSessionSettings}
                          disabled={savingSessionSettings}
                          className="w-full bg-indigo-600 hover:bg-indigo-700"
                        >
                          {savingSessionSettings ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Save Settings
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Active Sessions Summary */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden lg:col-span-2">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-emerald-600" />
                        Active Sessions
                      </h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={loadActiveSessions}
                        className="text-slate-500 hover:text-slate-700"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-4">
                    {activeSessions && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm text-slate-600">Total Active Sessions</span>
                            <span className="font-bold text-xl text-emerald-600">{activeSessions.total_active_sessions ?? 0}</span>
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm text-slate-600">Users Online</span>
                            <span className="font-bold text-xl text-indigo-600">{activeSessions.users_with_sessions ?? 0}</span>
                          </div>
                        </div>
                        
                        {activeSessions.sessions_by_user?.length > 0 && (
                          <div className="mt-4">
                            <p className="text-sm font-medium text-slate-700 mb-3">Session Details by User:</p>
                            <div className="space-y-4 max-h-96 overflow-y-auto">
                              {activeSessions.sessions_by_user.map((user) => (
                                <div key={user.user_id} className="border border-slate-200 rounded-lg overflow-hidden">
                                  {/* User Header */}
                                  <div className="flex items-center justify-between p-3 bg-slate-50 border-b border-slate-200">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                                        <span className="text-indigo-600 font-semibold text-sm">
                                          {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                      <div>
                                        <p className="text-sm font-medium text-slate-800">{user.email}</p>
                                        <p className="text-xs text-slate-500">{user.session_count} active session{user.session_count > 1 ? 's' : ''}</p>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => forceLogoutUser(user.user_id, user.email)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      title="Force logout all sessions"
                                    >
                                      <X className="w-4 h-4 mr-1" />
                                      Logout All
                                    </Button>
                                  </div>
                                  
                                  {/* Sessions List */}
                                  <div className="divide-y divide-slate-100">
                                    {user.sessions?.map((session, idx) => (
                                      <div key={idx} className="p-3 hover:bg-slate-50 transition-colors group">
                                        <div className="flex items-center gap-3">
                                          {/* Session Info Grid */}
                                          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                                            {/* Device Type & Name */}
                                            <div className="flex items-center gap-2">
                                              {session.device_type === 'Mobile' ? (
                                                <Smartphone className="w-4 h-4 text-blue-500" />
                                              ) : session.device_type === 'Tablet' ? (
                                                <Tablet className="w-4 h-4 text-purple-500" />
                                              ) : (
                                                <Monitor className="w-4 h-4 text-slate-500" />
                                              )}
                                              <div>
                                                <p className="text-sm font-medium text-slate-700">
                                                  {session.device_name || session.device_type || 'Unknown Device'}
                                                </p>
                                                <p className="text-xs text-slate-400">{session.device_type || 'Device'}</p>
                                              </div>
                                            </div>
                                            
                                            {/* Browser & OS */}
                                            <div className="flex items-center gap-2">
                                              <Globe className="w-4 h-4 text-emerald-500" />
                                              <div>
                                                <p className="text-sm text-slate-700">{session.browser || 'Unknown Browser'}</p>
                                                <p className="text-xs text-slate-400">{session.os || 'Unknown OS'}</p>
                                              </div>
                                            </div>
                                            
                                            {/* IP Address */}
                                            <div className="flex items-center gap-2">
                                              <Shield className="w-4 h-4 text-amber-500" />
                                              <div>
                                                <p className="text-sm font-mono text-slate-700">{session.ip_address || 'Unknown'}</p>
                                                <p className="text-xs text-slate-400">IP Address</p>
                                              </div>
                                            </div>
                                            
                                            {/* Login Time */}
                                            <div className="flex items-center gap-2">
                                              <Clock className="w-4 h-4 text-slate-400" />
                                              <div>
                                                <p className="text-sm text-slate-700">
                                                  {session.created_at ? new Date(session.created_at).toLocaleDateString() : 'Unknown'}
                                                </p>
                                                <p className="text-xs text-slate-400">
                                                  {session.created_at ? new Date(session.created_at).toLocaleTimeString() : ''}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Individual Session Logout Button */}
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => forceLogoutSession(
                                              user.user_id, 
                                              idx, 
                                              session.browser || session.device_type || 'Unknown'
                                            )}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                            title="Logout this session"
                                          >
                                            <X className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Rate Limits Info */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Gauge className="w-4 h-4 text-teal-600" />
                      Rate Limit Configuration
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Request limits per 60-second window
                    </p>
                  </div>
                  <div className="p-4">
                    <div className="space-y-3">
                      {[
                        { endpoint: 'Login', limit: 5, color: 'bg-red-100 text-red-700' },
                        { endpoint: 'Register', limit: 3, color: 'bg-red-100 text-red-700' },
                        { endpoint: 'Auth (general)', limit: 10, color: 'bg-orange-100 text-orange-700' },
                        { endpoint: 'AI Explain', limit: 20, color: 'bg-amber-100 text-amber-700' },
                        { endpoint: 'Admin', limit: 30, color: 'bg-violet-100 text-violet-700' },
                        { endpoint: 'Quiz', limit: 60, color: 'bg-teal-100 text-teal-700' },
                        { endpoint: 'Default', limit: 120, color: 'bg-slate-100 text-slate-700' },
                      ].map((item) => (
                        <div key={item.endpoint} className="flex items-center justify-between">
                          <span className="text-sm text-slate-700">{item.endpoint}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${item.color}`}>
                            {item.limit} req/min
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Blocked Sources & Email Alerts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Blocked Sources */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-red-600" />
                      Top Blocked Sources (Last Hour)
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      IPs/Users hitting rate limits most frequently
                    </p>
                  </div>
                  <div className="p-4 max-h-64 overflow-y-auto">
                    {serverStats?.rate_limit_blocks?.top_blocked?.length > 0 ? (
                      <div className="space-y-2">
                        {serverStats.rate_limit_blocks.top_blocked.map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-3 py-2">
                            <code className="text-xs font-mono text-slate-700 truncate flex-1 mr-4">
                              {item.identifier}
                            </code>
                            <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-medium">
                              {item.count} blocks
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                        <p className="text-sm text-slate-500">No blocked requests in the last hour</p>
                        <p className="text-xs text-slate-400 mt-1">Your site is running smoothly!</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Email Alerts Configuration */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Bell className="w-4 h-4 text-violet-600" />
                      Email Alerts
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Get notified when unusual activity is detected
                    </p>
                  </div>
                  <div className="p-4">
                    <div className="space-y-4">
                      <div className="bg-violet-50 rounded-lg p-4 border border-violet-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Mail className="w-4 h-4 text-violet-600" />
                          <span className="font-medium text-slate-800">Alert Recipient</span>
                        </div>
                        <p className="text-sm text-violet-700 font-mono">abapreplab@mail.com</p>
                      </div>
                      
                      <div className="space-y-2">
                        <p className="text-sm text-slate-600">
                          <strong>Alert triggers when:</strong>
                        </p>
                        <ul className="text-sm text-slate-600 space-y-1 ml-4">
                          <li>• 50+ requests blocked in 1 hour</li>
                          <li>• Cooldown: 15 minutes between alerts</li>
                        </ul>
                      </div>
                      
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={sendTestAlert}
                        disabled={sendingTestAlert}
                      >
                        {sendingTestAlert ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Test Alert
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cache TTL Info */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Cache Time-to-Live (TTL) Settings</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    How long data stays cached before refresh
                  </p>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                    {[
                      { name: 'Quiz List', ttl: '5 min', icon: '📋' },
                      { name: 'Quiz Detail', ttl: '10 min', icon: '📝' },
                      { name: 'Dictionary', ttl: '1 hour', icon: '📖' },
                      { name: 'Free Quiz', ttl: '30 min', icon: '🎯' },
                      { name: 'User Stats', ttl: '1 min', icon: '📊' },
                      { name: 'AI Explain', ttl: '24 hours', icon: '🤖' },
                      { name: 'Categories', ttl: '1 hour', icon: '🏷️' },
                    ].map((item) => (
                      <div key={item.name} className="bg-slate-50 rounded-lg p-3 text-center">
                        <div className="text-2xl mb-1">{item.icon}</div>
                        <div className="text-xs font-medium text-slate-700">{item.name}</div>
                        <div className="text-sm font-bold text-violet-600">{item.ttl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Mock Exam Expansion */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-100">
                      <FileText className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Mock Exam Expansion</h3>
                      <p className="text-sm text-slate-500">Add 35 questions to each mock exam</p>
                    </div>
                  </div>
                  <Button
                    onClick={expandMockExams}
                    disabled={expandingMocks}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    {expandingMocks ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Expanding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Expand Mock Exams
                      </>
                    )}
                  </Button>
                </div>
                
                {mockExamStatus && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {(mockExamStatus.mock_exams || []).map((exam) => (
                        <div key={exam.id} className="bg-slate-50 rounded-lg p-3">
                          <p className="text-xs text-slate-500 truncate">{exam.title.replace('BCBA ', '')}</p>
                          <p className="text-lg font-semibold text-slate-800">{exam.question_count}</p>
                          <p className="text-xs text-slate-500">questions</p>
                          {exam.has_expansion && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 mt-1">
                              <Check className="w-3 h-3 mr-1" />
                              Expanded
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-slate-600">
                      Total mock exam questions: <strong>{mockExamStatus.total_questions ?? 0}</strong>
                    </p>
                  </div>
                )}
              </div>

              {/* Clinical Vignettes Import */}
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-emerald-100">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Clinical Vignettes</h3>
                      <p className="text-sm text-slate-500">Import or adjust scenario-based questions</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={importVignettes}
                      disabled={importingVignettes || !vignettesStatus?.vignettes_loaded}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {importingVignettes ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Import Vignettes
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={() => setShowVignetteAdjust(true)}
                      variant="outline"
                      className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Adjust
                    </Button>
                  </div>
                </div>
                
                {vignettesStatus && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {(vignettesStatus.exams || []).map((exam) => (
                        <div 
                          key={exam.id} 
                          className="bg-slate-50 rounded-lg p-3 cursor-pointer hover:bg-slate-100 transition-colors"
                          onClick={() => {
                            setSelectedExam(exam);
                            setTargetPercentage(Math.round(exam.with_vignettes / exam.total_questions * 100));
                            setShowVignetteAdjust(true);
                          }}
                        >
                          <p className="text-xs text-slate-500 truncate">{exam.title.replace('BCBA ', '')}</p>
                          <p className="text-lg font-semibold text-slate-800">{exam.with_vignettes}/{exam.total_questions}</p>
                          <p className="text-xs text-slate-500">vignettes ({Math.round(exam.with_vignettes / exam.total_questions * 100)}%)</p>
                          {exam.with_vignettes >= exam.available_to_import && exam.available_to_import > 0 ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700 mt-1">
                              <Check className="w-3 h-3 mr-1" />
                              Complete
                            </span>
                          ) : exam.needs_import ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700 mt-1">
                              {exam.available_to_import} available
                            </span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">Click on an exam to adjust its vignette percentage</p>
                    {!vignettesStatus.vignettes_loaded && (
                      <p className="text-sm text-amber-600">⚠️ Vignettes data not loaded. Redeploy may be needed.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Quizzes Import Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FileText className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Quizzes</h3>
                      <p className="text-sm text-slate-500">Import quiz data for practice tests</p>
                    </div>
                  </div>
                  <Button
                    onClick={importQuizzes}
                    disabled={importingQuizzes || (quizzesStatus && !quizzesStatus.needs_import)}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {importingQuizzes ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Import Quizzes
                      </>
                    )}
                  </Button>
                </div>
                
                {quizzesStatus && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">In Database</p>
                        <p className="text-2xl font-semibold text-slate-800">{quizzesStatus.database_count}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3">
                        <p className="text-xs text-slate-500">Available to Import</p>
                        <p className="text-2xl font-semibold text-purple-600">{quizzesStatus.bundled_count}</p>
                      </div>
                    </div>
                    
                    {quizzesStatus.needs_import ? (
                      <p className="text-sm text-amber-600 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Quizzes need to be imported. Click "Import Quizzes" above.
                      </p>
                    ) : (
                      <p className="text-sm text-emerald-600 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        All {quizzesStatus.database_count} quizzes imported!
                      </p>
                    )}
                    
                    {quizzesStatus.categories && Object.keys(quizzesStatus.categories).length > 0 && (
                      <div className="mt-2">
                        <p className="text-xs text-slate-500 mb-2">Categories in database:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(quizzesStatus.categories).map(([cat, count]) => (
                            <span key={cat} className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full">
                              {cat}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Flashcards Import Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="flashcards-import-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-teal-100 rounded-lg">
                      <BookOpen className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800">Flashcards</h3>
                      <p className="text-sm text-slate-500">Import 400 ABA flashcards for study</p>
                    </div>
                  </div>
                  <Button
                    onClick={importFlashcards}
                    disabled={importingFlashcards || (flashcardsStatus && flashcardsStatus.imported)}
                    className="bg-teal-600 hover:bg-teal-700"
                    data-testid="import-flashcards-btn"
                  >
                    {importingFlashcards ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4 mr-2" />
                        Import Flashcards
                      </>
                    )}
                  </Button>
                </div>
                
                <div className="space-y-3">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-slate-500">Cards Available</p>
                    <p className="text-2xl font-semibold text-teal-600">400</p>
                  </div>
                  
                  {flashcardsStatus?.imported ? (
                    <p className="text-sm text-emerald-600 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      All {flashcardsStatus.count || 400} flashcards imported!
                    </p>
                  ) : (
                    <p className="text-sm text-amber-600 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Flashcards need to be imported for the feature to work.
                    </p>
                  )}
                </div>
              </div>

              {/* Vignette Adjustment Modal */}
              {showVignetteAdjust && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      Adjust Vignettes
                    </h3>
                    
                    {/* Exam Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Select Mock Exam
                      </label>
                      <select
                        value={selectedExam?.id || ""}
                        onChange={(e) => {
                          const exam = vignettesStatus?.exams.find(ex => ex.id === e.target.value);
                          setSelectedExam(exam);
                          if (exam) {
                            setTargetPercentage(Math.round(exam.with_vignettes / exam.total_questions * 100));
                          }
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      >
                        <option value="">Select an exam...</option>
                        {vignettesStatus?.exams.map((exam) => (
                          <option key={exam.id} value={exam.id}>
                            {exam.title} ({exam.with_vignettes}/{exam.total_questions})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {selectedExam && (
                      <>
                        {/* Current Status */}
                        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600">
                            Current: <strong>{selectedExam.with_vignettes}</strong> vignettes 
                            ({Math.round(selectedExam.with_vignettes / selectedExam.total_questions * 100)}%)
                          </p>
                          <p className="text-sm text-slate-600">
                            Available: <strong>{selectedExam.available_to_import}</strong> vignettes
                          </p>
                        </div>
                        
                        {/* Percentage Slider */}
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-slate-700 mb-2">
                            Target Percentage: <strong>{targetPercentage}%</strong> 
                            ({Math.round(selectedExam.total_questions * targetPercentage / 100)} questions)
                          </label>
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={targetPercentage}
                            onChange={(e) => setTargetPercentage(parseInt(e.target.value))}
                            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                          />
                          <div className="flex justify-between text-xs text-slate-500 mt-1">
                            <span>0%</span>
                            <span>50%</span>
                            <span>100%</span>
                          </div>
                        </div>
                        
                        {/* Quick Presets */}
                        <div className="mb-4">
                          <p className="text-sm text-slate-600 mb-2">Quick presets:</p>
                          <div className="flex gap-2">
                            {[50, 75, 80, 90, 100].map((pct) => (
                              <button
                                key={pct}
                                onClick={() => setTargetPercentage(pct)}
                                className={`px-3 py-1 rounded text-sm ${
                                  targetPercentage === pct 
                                    ? 'bg-emerald-600 text-white' 
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                }`}
                              >
                                {pct}%
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-6">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowVignetteAdjust(false);
                          setSelectedExam(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={adjustVignettes}
                        disabled={!selectedExam || adjustingVignettes}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {adjustingVignettes ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Adjusting...
                          </>
                        ) : (
                          "Apply Changes"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Performance Tips */}
              <div className="bg-gradient-to-r from-violet-50 to-teal-50 rounded-xl border border-violet-200 p-6">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-violet-600" />
                  Performance Tips
                </h3>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span><strong>AI Explanations Cache:</strong> Repeated questions get instant responses (24hr cache)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Rate Limiting:</strong> Protects against abuse and ensures fair usage</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Quiz Caching:</strong> Reduces database load for frequently accessed content</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span><strong>Auto-Cleanup:</strong> Expired cache entries are removed every 5 minutes</span>
                  </li>
                </ul>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-6">
              {/* Search Bar */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <form onSubmit={handleUserSearch} className="flex gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by email or name..."
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Button type="submit" className="bg-violet-600 hover:bg-violet-700">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                  {userSearch && (
                    <Button type="button" variant="outline" onClick={() => { setUserSearch(""); loadUsers(1, ""); }}>
                      Clear
                    </Button>
                  )}
                </form>
              </div>

              {/* Users Table */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                      <Users className="w-4 h-4 text-violet-600" />
                      Registered Users
                      <Badge variant="secondary">{usersTotal} total</Badge>
                    </h3>
                    <Button variant="ghost" size="sm" onClick={() => loadUsers(usersPage, userSearch)} disabled={usersLoading}>
                      <RefreshCw className={`w-4 h-4 ${usersLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>

                {usersLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
                  </div>
                ) : users.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mb-4 opacity-50" />
                    <p>No users found</p>
                    {userSearch && <p className="text-sm">Try a different search term</p>}
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50 border-b border-slate-200">
                          <tr>
                            <th className="text-left p-4 font-medium text-slate-600">User</th>
                            <th className="text-left p-4 font-medium text-slate-600">Status</th>
                            <th className="text-left p-4 font-medium text-slate-600">Quizzes</th>
                            <th className="text-left p-4 font-medium text-slate-600">Sessions</th>
                            <th className="text-left p-4 font-medium text-slate-600">Joined</th>
                            <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {users.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                              <td className="p-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                                    <span className="text-violet-600 font-semibold">
                                      {u.name?.charAt(0).toUpperCase() || u.email?.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <div>
                                    <p className="font-medium text-slate-800">{u.name || 'No name'}</p>
                                    <p className="text-sm text-slate-500">{u.email}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center gap-2">
                                  {u.is_premium ? (
                                    <Badge className="bg-amber-100 text-amber-700">
                                      <Crown className="w-3 h-3 mr-1" />
                                      Premium
                                    </Badge>
                                  ) : (
                                    <Badge variant="secondary">Free</Badge>
                                  )}
                                  {u.is_admin && (
                                    <Badge className="bg-red-100 text-red-700">
                                      <Shield className="w-3 h-3 mr-1" />
                                      Admin
                                    </Badge>
                                  )}
                                </div>
                              </td>
                              <td className="p-4">
                                <span className="text-slate-700">{u.quiz_attempts || 0}</span>
                              </td>
                              <td className="p-4">
                                <Badge variant={u.active_sessions > 0 ? "default" : "secondary"} className={u.active_sessions > 0 ? "bg-green-100 text-green-700" : ""}>
                                  {u.active_sessions || 0} active
                                </Badge>
                              </td>
                              <td className="p-4 text-sm text-slate-500">
                                {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'Unknown'}
                              </td>
                              <td className="p-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => viewUserDetails(u.id)}
                                    title="View details"
                                  >
                                    <User className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => toggleUserPremium(u.id, u.email)}
                                    title={u.is_premium ? "Remove premium" : "Grant premium"}
                                    className={u.is_premium ? "text-amber-600 hover:text-amber-700" : "text-slate-600"}
                                  >
                                    <Crown className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { viewUserDetails(u.id); setTimeout(() => setShowResetPasswordDialog(true), 300); }}
                                    title="Reset password"
                                    className="text-blue-600 hover:text-blue-700"
                                  >
                                    <Key className="w-4 h-4" />
                                  </Button>
                                  {!u.is_admin && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteUser(u.id, u.email)}
                                      title="Delete user"
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="p-4 border-t border-slate-200 flex items-center justify-between">
                      <p className="text-sm text-slate-500">
                        Showing {(usersPage - 1) * 20 + 1} - {Math.min(usersPage * 20, usersTotal)} of {usersTotal} users
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadUsers(usersPage - 1, userSearch)}
                          disabled={usersPage <= 1}
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm text-slate-600">Page {usersPage}</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => loadUsers(usersPage + 1, userSearch)}
                          disabled={usersPage * 20 >= usersTotal}
                        >
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Quiz Dialog */}
      <Dialog open={showQuizDialog} onOpenChange={setShowQuizDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedQuiz ? "Edit Quiz" : "Create New Quiz"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Title</label>
              <Input value={quizForm.title} onChange={(e) => setQuizForm({ ...quizForm, title: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea value={quizForm.description} onChange={(e) => setQuizForm({ ...quizForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Category</label>
                <Input value={quizForm.category} onChange={(e) => setQuizForm({ ...quizForm, category: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Time Limit (min)</label>
                <Input type="number" value={quizForm.time_limit} onChange={(e) => setQuizForm({ ...quizForm, time_limit: parseInt(e.target.value) || 10 })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Difficulty</label>
                <Select value={quizForm.difficulty} onValueChange={(v) => setQuizForm({ ...quizForm, difficulty: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch checked={quizForm.is_mock_exam} onCheckedChange={(v) => setQuizForm({ ...quizForm, is_mock_exam: v })} />
                <label className="text-sm">Mock Exam</label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuizDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveQuiz} className="bg-violet-600 hover:bg-violet-700">Save Quiz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? "Edit Question" : "Add Question"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Question Text</label>
              <Textarea value={questionForm.text} onChange={(e) => setQuestionForm({ ...questionForm, text: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-sm font-medium">Question Type</label>
              <Select value={questionForm.question_type} onValueChange={(v) => setQuestionForm({ ...questionForm, question_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                  <SelectItem value="true_false">True/False</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Options</label>
              <div className="space-y-2">
                {questionForm.options.map((opt, i) => (
                  <Input
                    key={i}
                    placeholder={`Option ${i + 1}`}
                    value={opt}
                    onChange={(e) => {
                      const newOptions = [...questionForm.options];
                      newOptions[i] = e.target.value;
                      setQuestionForm({ ...questionForm, options: newOptions });
                    }}
                  />
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Correct Answer</label>
              <Input value={questionForm.correct_answer} onChange={(e) => setQuestionForm({ ...questionForm, correct_answer: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Explanation (optional)</label>
              <Textarea value={questionForm.explanation} onChange={(e) => setQuestionForm({ ...questionForm, explanation: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuestionDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveQuestion} className="bg-violet-600 hover:bg-violet-700">Save Question</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Acronym Dialog */}
      <Dialog open={showAcronymDialog} onOpenChange={setShowAcronymDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingAcronym ? "Edit Acronym" : "Add Acronym"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Acronym</label>
              <Input value={acronymForm.acronym} onChange={(e) => setAcronymForm({ ...acronymForm, acronym: e.target.value })} disabled={!!editingAcronym} />
            </div>
            <div>
              <label className="text-sm font-medium">Full Name</label>
              <Input value={acronymForm.full} onChange={(e) => setAcronymForm({ ...acronymForm, full: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea value={acronymForm.description} onChange={(e) => setAcronymForm({ ...acronymForm, description: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcronymDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveAcronym} className="bg-violet-600 hover:bg-violet-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Term Dialog */}
      <Dialog open={showTermDialog} onOpenChange={setShowTermDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTerm ? "Edit Term" : "Add Term"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Term</label>
              <Input value={termForm.term} onChange={(e) => setTermForm({ ...termForm, term: e.target.value })} disabled={!!editingTerm} />
            </div>
            <div>
              <label className="text-sm font-medium">Definition</label>
              <Textarea value={termForm.definition} onChange={(e) => setTermForm({ ...termForm, definition: e.target.value })} rows={3} />
            </div>
            <div>
              <label className="text-sm font-medium">Citation (APA)</label>
              <Textarea value={termForm.citation} onChange={(e) => setTermForm({ ...termForm, citation: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTermDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveTerm} className="bg-violet-600 hover:bg-violet-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Quiz</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteTarget?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteQuiz}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                <span className="text-violet-600 font-semibold">
                  {selectedUser?.user?.name?.charAt(0).toUpperCase() || selectedUser?.user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p>{selectedUser?.user?.name || 'No name'}</p>
                <p className="text-sm font-normal text-slate-500">{selectedUser?.user?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <div className="flex items-center gap-2">
                    {selectedUser.user.is_premium ? (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Crown className="w-3 h-3 mr-1" />
                        Premium
                      </Badge>
                    ) : (
                      <Badge variant="secondary">Free</Badge>
                    )}
                    {selectedUser.user.is_admin && (
                      <Badge className="bg-red-100 text-red-700">Admin</Badge>
                    )}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Member Since</p>
                  <p className="font-medium">{selectedUser.user.created_at ? new Date(selectedUser.user.created_at).toLocaleDateString() : 'Unknown'}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Quiz Attempts</p>
                  <p className="font-medium">{selectedUser.recent_attempts?.length || 0}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Active Sessions</p>
                  <p className="font-medium">{selectedUser.active_sessions?.length || 0}</p>
                </div>
              </div>

              {/* Active Sessions */}
              {selectedUser.active_sessions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Active Sessions</h4>
                  <div className="space-y-2">
                    {selectedUser.active_sessions.map((session, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                        <div className="flex items-center gap-3">
                          {session.device_type === 'Mobile' ? (
                            <Smartphone className="w-4 h-4 text-blue-500" />
                          ) : (
                            <Monitor className="w-4 h-4 text-slate-500" />
                          )}
                          <div>
                            <p className="font-medium">{session.browser || 'Unknown Browser'}</p>
                            <p className="text-xs text-slate-400">{session.os || 'Unknown OS'} • {session.ip_address || 'Unknown IP'}</p>
                          </div>
                        </div>
                        <p className="text-xs text-slate-400">
                          {session.created_at ? new Date(session.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Quiz Attempts */}
              {selectedUser.recent_attempts?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Recent Quiz Attempts</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedUser.recent_attempts.slice(0, 5).map((attempt, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-sm">
                        <p className="font-medium">{attempt.quiz_title || 'Quiz'}</p>
                        <div className="flex items-center gap-3">
                          <Badge className={attempt.score >= 70 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {attempt.score}%
                          </Badge>
                          <p className="text-xs text-slate-400">
                            {attempt.created_at ? new Date(attempt.created_at).toLocaleDateString() : ''}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowResetPasswordDialog(true)}
                  className="flex-1"
                >
                  <Key className="w-4 h-4 mr-2" />
                  Reset Password
                </Button>
                <Button
                  variant="outline"
                  onClick={() => toggleUserPremium(selectedUser.user.id, selectedUser.user.email)}
                  className="flex-1"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {selectedUser.user.is_premium ? 'Remove Premium' : 'Grant Premium'}
                </Button>
                {!selectedUser.user.is_admin && (
                  <Button
                    variant="destructive"
                    onClick={() => deleteUser(selectedUser.user.id, selectedUser.user.email)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.user?.email}. The user will be logged out of all devices.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">New Password</label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowResetPasswordDialog(false); setNewPassword(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={resetUserPassword} 
              disabled={resettingPassword || newPassword.length < 6}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {resettingPassword ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resetting...</>
              ) : (
                <><Key className="w-4 h-4 mr-2" /> Reset Password</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
